'use client';

import React, { useState } from 'react';
import { Review } from '@/lib/Apis/models';
import { ReviewCard } from './ReviewCard';
import { Star } from 'lucide-react';

interface ReviewsListProps {
  reviews: Review[];
  isLoading?: boolean;
}

const PAGE_SIZE = 5;

export function ReviewsList({ reviews, isLoading }: ReviewsListProps) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const sortedReviews = React.useMemo(() => {
    return [...reviews].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [reviews]);

  const visibleReviews = sortedReviews.slice(0, visibleCount);
  const hasMore = visibleCount < sortedReviews.length;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="border border-gray-200 rounded-xl p-5 bg-white animate-pulse"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full" />
                <div>
                  <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
                  <div className="h-3 w-16 bg-gray-200 rounded" />
                </div>
              </div>
              <div className="h-4 w-20 bg-gray-200 rounded" />
            </div>
            <div className="space-y-2">
              <div className="h-3 w-full bg-gray-200 rounded" />
              <div className="h-3 w-3/4 bg-gray-200 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (sortedReviews.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
        <Star className="size-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 font-medium mb-1">No reviews yet</p>
        <p className="text-sm text-gray-500">Be the first to review this product!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {visibleReviews.map((review) => (
        <ReviewCard key={review.id || review._id} review={review} />
      ))}

      {hasMore && (
        <div className="text-center pt-2">
          <button
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            className="px-6 py-2.5 border border-gray-300 rounded-lg text-[13px] font-medium text-hsecondary hover:border-hsecondary transition-colors"
          >
            Show More Reviews
          </button>
        </div>
      )}
    </div>
  );
}
